import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';

// Auth
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import BrandScan from './pages/auth/BrandScan';
import BrandPreview from './pages/auth/BrandPreview';
import EmailVerification from './pages/auth/EmailVerification';
import InvitationAcceptance from './pages/auth/InvitationAcceptance';
import PasswordReset from './pages/auth/PasswordReset';
import SeniorManagerOnboarding from './pages/auth/SeniorManagerOnboarding';
import ManagerOnboarding from './pages/auth/ManagerOnboarding';
import FuelManagerOnboarding from './pages/auth/FuelManagerOnboarding';

// Dashboard
import ManagerDashboard from './pages/dashboard/ManagerDashboard';
import GroupOverview from './pages/dashboard/GroupOverview';
import FuelManagerDashboard from './pages/dashboard/FuelManagerDashboard';

// Assets
import AssetList from './pages/assets/AssetList';
import AssetDetail from './pages/assets/AssetDetail';

// Staff
import StaffManagement from './pages/staff/StaffManagement';
import StaffProfile from './pages/staff/StaffProfile';
import StaffInvite from './pages/staff/StaffInvite';

// Incidents
import IncidentList from './pages/incidents/IncidentList';
import IncidentDetail from './pages/incidents/IncidentDetail';

// Checks
import CheckHistory from './pages/checks/CheckHistory';
import CheckDetail from './pages/checks/CheckDetail';

// Fuel
import DervOverview from './pages/fuel/DervOverview';
import DervDetail from './pages/fuel/DervDetail';

// Settings
import Branches from './pages/settings/Branches';
import Departments from './pages/settings/Departments';
import Roles from './pages/settings/Roles';
import Templates from './pages/settings/Templates';
import TemplateEditor from './pages/settings/TemplateEditor';
import Notifications from './pages/settings/Notifications';
import Organisation from './pages/settings/Organisation';
import DervSettings from './pages/settings/DervSettings';
import AuditLog from './pages/settings/AuditLog';

// Profile
import PersonalDetails from './pages/profile/PersonalDetails';
import ProfileNotifications from './pages/profile/ProfileNotifications';
import EmailOutlook from './pages/profile/EmailOutlook';
import Security from './pages/profile/Security';
import ActiveSessions from './pages/profile/ActiveSessions';

// Billing
import BillingSettings from './pages/billing/BillingSettings';

const SM = ['super_admin', 'senior_manager'];
const MGR = ['super_admin', 'senior_manager', 'manager'];
const FUEL = ['super_admin', 'senior_manager', 'fuel_manager'];
const ALL = ['super_admin', 'senior_manager', 'manager', 'fuel_manager', 'operator', 'driver'];

function App() {
  return (
    <BrowserRouter>
      <Routes>

        {/* Public routes */}
        <Route path='/login' element={<Login />} />
        <Route path='/signup' element={<Signup />} />
        <Route path='/signup/scan' element={<BrandScan />} />
        <Route path='/signup/preview' element={<BrandPreview />} />
        <Route path='/verify-email' element={<EmailVerification />} />
        <Route path='/invite/:token' element={<InvitationAcceptance />} />
        <Route path='/reset-password' element={<PasswordReset />} />

        {/* Onboarding */}
        <Route path='/onboarding/senior' element={
          <ProtectedRoute allowedRoles={SM}>
            <SeniorManagerOnboarding />
          </ProtectedRoute>
        } />
        <Route path='/onboarding/manager' element={
          <ProtectedRoute allowedRoles={MGR}>
            <ManagerOnboarding />
          </ProtectedRoute>
        } />
        <Route path='/onboarding/fuel' element={
          <ProtectedRoute allowedRoles={FUEL}>
            <FuelManagerOnboarding />
          </ProtectedRoute>
        } />

        {/* Dashboards */}
        <Route path='/dashboard' element={
          <ProtectedRoute allowedRoles={MGR}>
            <ManagerDashboard />
          </ProtectedRoute>
        } />
        <Route path='/group' element={
          <ProtectedRoute allowedRoles={SM}>
            <GroupOverview />
          </ProtectedRoute>
        } />
        <Route path='/fuel-dashboard' element={
          <ProtectedRoute allowedRoles={FUEL}>
            <FuelManagerDashboard />
          </ProtectedRoute>
        } />

        {/* Assets */}
        <Route path='/assets' element={
          <ProtectedRoute allowedRoles={MGR}>
            <AssetList />
          </ProtectedRoute>
        } />
        <Route path='/assets/:assetId' element={
          <ProtectedRoute allowedRoles={MGR}>
            <AssetDetail />
          </ProtectedRoute>
        } />

        {/* Staff */}
        <Route path='/staff' element={
          <ProtectedRoute allowedRoles={MGR}>
            <StaffManagement />
          </ProtectedRoute>
        } />
        <Route path='/staff/invite' element={
          <ProtectedRoute allowedRoles={MGR}>
            <StaffInvite />
          </ProtectedRoute>
        } />
        <Route path='/staff/:userId' element={
          <ProtectedRoute allowedRoles={MGR}>
            <StaffProfile />
          </ProtectedRoute>
        } />

        {/* Incidents */}
        <Route path='/incidents' element={
          <ProtectedRoute allowedRoles={MGR}>
            <IncidentList />
          </ProtectedRoute>
        } />
        <Route path='/incidents/:incidentId' element={
          <ProtectedRoute allowedRoles={MGR}>
            <IncidentDetail />
          </ProtectedRoute>
        } />

        {/* Checks */}
        <Route path='/checks' element={
          <ProtectedRoute allowedRoles={[...MGR, 'fuel_manager']}>
            <CheckHistory />
          </ProtectedRoute>
        } />
        <Route path='/checks/:checkId' element={
          <ProtectedRoute allowedRoles={[...MGR, 'fuel_manager']}>
            <CheckDetail />
          </ProtectedRoute>
        } />

        {/* Fuel */}
        <Route path='/fuel' element={
          <ProtectedRoute allowedRoles={FUEL}>
            <DervOverview />
          </ProtectedRoute>
        } />
        <Route path='/fuel/:tankId' element={
          <ProtectedRoute allowedRoles={FUEL}>
            <DervDetail />
          </ProtectedRoute>
        } />

        {/* Settings */}
        <Route path='/settings/branches' element={
          <ProtectedRoute allowedRoles={SM}>
            <Branches />
          </ProtectedRoute>
        } />
        <Route path='/settings/departments' element={
          <ProtectedRoute allowedRoles={SM}>
            <Departments />
          </ProtectedRoute>
        } />
        <Route path='/settings/roles' element={
          <ProtectedRoute allowedRoles={SM}>
            <Roles />
          </ProtectedRoute>
        } />
        <Route path='/settings/templates' element={
          <ProtectedRoute allowedRoles={SM}>
            <Templates />
          </ProtectedRoute>
        } />
        <Route path='/settings/templates/:templateId' element={
          <ProtectedRoute allowedRoles={SM}>
            <TemplateEditor />
          </ProtectedRoute>
        } />
        <Route path='/settings/notifications' element={
          <ProtectedRoute allowedRoles={SM}>
            <Notifications />
          </ProtectedRoute>
        } />
        <Route path='/settings/organisation' element={
          <ProtectedRoute allowedRoles={SM}>
            <Organisation />
          </ProtectedRoute>
        } />
        <Route path='/settings/derv' element={
          <ProtectedRoute allowedRoles={[...SM, 'fuel_manager']}>
            <DervSettings />
          </ProtectedRoute>
        } />
        <Route path='/settings/audit' element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AuditLog />
          </ProtectedRoute>
        } />

        {/* Profile */}
        <Route path='/profile' element={
          <ProtectedRoute allowedRoles={ALL}>
            <PersonalDetails />
          </ProtectedRoute>
        } />
        <Route path='/profile/notifications' element={
          <ProtectedRoute allowedRoles={ALL}>
            <ProfileNotifications />
          </ProtectedRoute>
        } />
        <Route path='/profile/outlook' element={
          <ProtectedRoute allowedRoles={ALL}>
            <EmailOutlook />
          </ProtectedRoute>
        } />
        <Route path='/profile/security' element={
          <ProtectedRoute allowedRoles={ALL}>
            <Security />
          </ProtectedRoute>
        } />
        <Route path='/profile/sessions' element={
          <ProtectedRoute allowedRoles={ALL}>
            <ActiveSessions />
          </ProtectedRoute>
        } />

        {/* Billing */}
        <Route path='/billing' element={
          <ProtectedRoute allowedRoles={SM}>
            <BillingSettings />
          </ProtectedRoute>
        } />

        {/* Default */}
        <Route path='/' element={<Navigate to='/login' replace />} />
        <Route path='*' element={<Navigate to='/login' replace />} />

      </Routes>
    </BrowserRouter>
  );
}

export default App;